import { ToolbarElement } from './ToolbarElement'

const entryTemplate = `
<div class="toolbar-dropdown-icon"></div>
<div class="toolbar-dropdown-arrow"></div>
`

interface IToolbarDropdownElementConfig<T> {
  items: { key: string; value: T; text: string; default?: boolean }[]
  overlayElementWidth: number
  entryElementHeight: number
  topOffset: number
}

export class ToolbarDropdownElement<T extends { toString: () => string }> extends ToolbarElement {
  public entryElement = document.createElement('div')
  public overlayElement = document.createElement('div')
  constructor(
    overlayContainer: HTMLDivElement,
    public readonly name: string,
    public readonly config: IToolbarDropdownElementConfig<T>,
  ) {
    super(overlayContainer)
    this.initDom()
    this.initEvent()
  }

  public getEntryElement(): HTMLElement {
    return this.entryElement
  }

  public override setFormat(format: { [key: string]: Set<any> }): void {
    this.overlayElement.querySelectorAll('.toolbar-dropdown-menu-item.selected').forEach((item) => {
      item.classList.remove('selected')
    })
    if (format[this.name] && format[this.name].size > 0) {
      this.overlayElement.querySelectorAll('.toolbar-dropdown-menu-item').forEach((node, index) => {
        if (format[this.name].has(this.config.items[index].value)) {
          node.classList.add('selected')
        }
      })
    } else {
      const defaultValue = this.config.items.find((item) => item.default)
      if (defaultValue) {
        this.overlayElement.querySelectorAll('.toolbar-dropdown-menu-item').forEach((item) => {
          if (defaultValue.value.toString() === (item as HTMLElement).dataset.value) {
            item.classList.add('selected')
          }
        })
      }
    }
  }

  protected initDom() {
    this.entryElement.classList.add(`toolbar-dropdown-${this.name}`, 'toolbar-dropdown')
    this.entryElement.innerHTML = entryTemplate

    this.overlayElement.classList.add(`toolbar-dropdown-menu-${this.name}`, 'toolbar-dropdown-menu')
    this.overlayElement.style.width = `${this.config.overlayElementWidth ?? 0}px`

    this.config.items.forEach((item) => {
      const itemElement = document.createElement('div')
      itemElement.classList.add(`toolbar-dropdown-menu-item-${item.key}`, 'toolbar-dropdown-menu-item')
      itemElement.innerText = item.text
      itemElement.dataset.value = item.value.toString()
      this.overlayElement.appendChild(itemElement)
    })
    this.overlayContainer.appendChild(this.overlayElement)
  }

  protected initEvent() {
    this.entryElement.addEventListener('mousedown', (event) => {
      event.preventDefault()
    })
    this.overlayElement.addEventListener('mousedown', (event) => {
      event.preventDefault()
    })
    this.entryElement.addEventListener('click', () => {
      this.showOverlay()
    })
    this.overlayElement.addEventListener('click', () => {
      this.hideOverlay()
    })
    this.overlayElement.querySelectorAll('.toolbar-dropdown-menu-item').forEach((item) => {
      item.addEventListener('click', (event) => {
        const target = event.target as HTMLDivElement
        const value = target.dataset.value
        this.emit(`choose`, value)
      })
    })
  }

  protected showOverlay() {
    const overlayElementWidth = this.config.overlayElementWidth ?? 0
    const entryElementHeight = this.config.entryElementHeight ?? 0
    const topOffset = this.config.topOffset ?? 0
    const entryBounding = this.entryElement.getBoundingClientRect()
    const overlayContainerBounding = this.overlayContainer.getBoundingClientRect()

    this.overlayElement.style.left = `${Math.max(
      Math.min(
        entryBounding.left - overlayContainerBounding.left - overlayElementWidth / 2 + entryBounding.width / 2,
        overlayContainerBounding.width - overlayElementWidth - 5,
      ),
      5,
    )}px`
    this.overlayElement.style.top = `${
      entryBounding.top - overlayContainerBounding.top + entryElementHeight + topOffset
    }px`
    this.overlayElement.style.display = 'block'
    document.addEventListener('mousedown', this.onDocumentMousedown, true)
  }

  protected hideOverlay() {
    this.overlayElement.style.display = 'none'
    document.removeEventListener('mousedown', this.onDocumentMousedown, true)
  }

  protected onDocumentMousedown = (event: MouseEvent) => {
    this.overlayElement.contains(event.target as Node) ||
      this.entryElement.contains(event.target as Node) ||
      this.hideOverlay()
  }
}
